import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import DataTable from "react-data-table-component";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy } from "@fortawesome/free-regular-svg-icons";
import { customStyles } from "../crm/TableStyle";
import "../css/OrganisationCompetitionSearch.css";
import { DataTableLoader, GoldLoader } from "../shared/Loaders";

/** String id for populated refs, raw ObjectId strings, or null/undefined. */
function refId(v) {
  if (v == null) return "";
  if (typeof v === "object" && v._id != null) return String(v._id);
  return String(v);
}

function norm(s) {
  if (s == null || s === "") return "";
  return String(s).trim().toLowerCase();
}

function firstOrgType(raw) {
  if (Array.isArray(raw)) return raw[0] || null;
  return raw || null;
}

function orgTypeLabelOf(org) {
  const ot = firstOrgType(org?.org_type_id);
  if (ot && typeof ot === "object") return ot.org_type || "Other";
  return "Other";
}

/** Org ids with an active orgcompetition row for the given competition only. */
function assignedOrgIdsForCompetition(assignedRows, competitionMongoId) {
  const cidTarget = competitionMongoId ? String(competitionMongoId) : "";
  if (!cidTarget) return new Set();
  return new Set(
    (assignedRows || [])
      .filter((r) => {
        const cid = String(r.competition_id?._id || r.competition_id || "");
        const st = (r.status || "active").toLowerCase();
        return cid === cidTarget && st === "active";
      })
      .map((r) => String(r.org_id?._id || r.org_id))
  );
}

function startsWithTerm(value, nt) {
  return norm(value).startsWith(nt);
}

function orgMatchesSearch(o, country, nt) {
  if (!nt) return true;
  return (
    startsWithTerm(o.name, nt) ||
    startsWithTerm(o.email, nt) ||
    startsWithTerm(o.email2, nt) ||
    startsWithTerm(o.short_name, nt) ||
    startsWithTerm(orgTypeLabelOf(o), nt) ||
    startsWithTerm(country?.code, nt)
  );
}

function personMatchesSearch(p, nt) {
  if (!p || !nt) return false;
  const one = (v) => startsWithTerm(v, nt);
  if (one(p.name) || one(p.mname) || one(p.lname) || one(p.preferred_name)) return true;
  if (one(p.email) || one(p.email2)) return true;
  const full = [p.name, p.mname, p.lname]
    .filter((x) => x != null && String(x).trim() !== "")
    .map((x) => String(x).trim())
    .join(" ");
  return full ? startsWithTerm(full, nt) : false;
}

const OrganisationCompetitionSearch = () => {
  const API_URL = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();

  const [organisations, setOrganisations] = useState([]);
  const [persons, setPersons] = useState([]);
  const [personRoles, setPersonRoles] = useState([]);
  const [countriesById, setCountriesById] = useState({});
  const [assignedOrgIds, setAssignedOrgIds] = useState(new Set());

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);

  const [competitionId, setCompetitionId] = useState("");
  const [loading, setLoading] = useState(true);

  const refreshAssignedOrgIds = useCallback(
    async (compId) => {
      const cid = compId || competitionId;
      if (!cid) {
        setAssignedOrgIds(new Set());
        return;
      }
      try {
        const assigned = await axios.get(`${API_URL}/ems/orgcompetitions`);
        setAssignedOrgIds(assignedOrgIdsForCompetition(assigned.data || [], cid));
      } catch (err) {
        console.error("Failed to refresh assigned organisations:", err);
      }
    },
    [API_URL, competitionId]
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [orgRes, countryRes, assignedRes, personRes, personRoleRes] =
          await Promise.all([
            axios.get(`${API_URL}/organisations`),
            axios.get(`${API_URL}/countries`),
            axios.get(`${API_URL}/ems/orgcompetitions`),
            axios.get(`${API_URL}/persons`),
            axios.get(`${API_URL}/personRoles`),
          ]);

        let activeComp = null;
        try {
          const activeRes = await axios.get(`${API_URL}/ems/competitions/active`);
          activeComp =
            activeRes?.data && activeRes.data._id && !activeRes.data.message
              ? activeRes.data
              : null;
        } catch (activeErr) {
          console.warn("Fallback to /ems/competitions?active=true:", activeErr);
          try {
            const legacyRes = await axios.get(`${API_URL}/ems/competitions?active=true`);
            const arr = Array.isArray(legacyRes?.data) ? legacyRes.data : [];
            activeComp = arr[0] || null;
          } catch (legacyErr) {
            console.error("Failed to resolve active competition:", legacyErr);
          }
        }

        setOrganisations(orgRes.data || []);
        setPersons(Array.isArray(personRes.data) ? personRes.data : []);
        setPersonRoles(Array.isArray(personRoleRes.data) ? personRoleRes.data : []);

        const map = {};
        (countryRes.data || []).forEach((c) => {
          if (c?._id) map[String(c._id)] = c;
        });
        setCountriesById(map);
        setCompetitionId(activeComp?._id || "");
        setAssignedOrgIds(assignedOrgIdsForCompetition(assignedRes.data || [], activeComp?._id));
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [API_URL]);

  const getCountry = useCallback(
    (org) => {
      const c = org?.country_id;
      if (c != null && typeof c === "object") return c;
      const raw = c != null && typeof c === "string" ? c : "";
      return raw ? countriesById[raw] : undefined;
    },
    [countriesById]
  );

  // ── PERSON → ORG LOOKUP ───────────────────────────────────────────────────
  const personsById = useMemo(() => {
    const m = {};
    persons.forEach((p) => { if (p?._id) m[String(p._id)] = p; });
    return m;
  }, [persons]);

  const orgIdsWithLinkedPerson = useMemo(() => {
    const t = (searchTerm || "").trim();
    if (!t) return new Set();
    const nt = norm(t);
    const out = new Set();
    personRoles.forEach((rel) => {
      const orgId = refId(rel?.org_id);
      const personId = refId(rel?.person_id);
      if (!orgId || !personId) return;
      if (personMatchesSearch(personsById[personId], nt)) out.add(orgId);
    });
    return out;
  }, [searchTerm, personRoles, personsById]);

  const filteredData = useMemo(() => {
    const t = (searchTerm || "").trim();
    const nt = t ? norm(t) : "";
    if (!nt) return organisations;
    return organisations.filter((o) => {
      const c = getCountry(o);
      return orgMatchesSearch(o, c, nt) || orgIdsWithLinkedPerson.has(String(o._id));
    });
  }, [organisations, searchTerm, getCountry, orgIdsWithLinkedPerson]);

  // ── CLIPBOARD ─────────────────────────────────────────────────────────────
  const copyToClipboard = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(String(text).trim());
    alert("Email copied to clipboard!");
  };

  // ── TABLE COLUMNS ─────────────────────────────────────────────────────────
  const columns = [
    {
      name: "Status",
      width: "70px",
      ignoreRowClick: true,
      cell: (row) =>
        assignedOrgIds.has(String(row._id)) ? (
          <span style={{ color: "green", fontSize: 18 }} title="Already assigned">✔</span>
        ) : null,
    },
    {
      name: "Logo",
      width: "90px",
      sortable: false,
      ignoreRowClick: true,
      cell: (row) => (
        <img
          src={row.logo ? `/images/${row.logo}` : "/images/org_logo1.png"}
          style={{ width: 48, height: 40, borderRadius: 4, objectFit: "cover" }}
          alt=""
        />
      ),
    },
    {
      name: "Name",
      sortable: true,
      selector: (row) => row.name || "",
      cell: (row) => (
        <span
          className="text-secondary fw-semibold org-search-name"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/organisation/${row._id}`);
          }}
        >
          {row.name || "—"}
        </span>
      ),
    },
    {
      name: "Org. Type",
      sortable: true,
      selector: (row) => orgTypeLabelOf(row) || "—",
    },
    {
      name: "Email",
      sortable: true,
      selector: (row) => row.email || "",
      cell: (row) => (
        <div className="org-search-email-cell" onClick={(e) => e.stopPropagation()}>
          <span className="org-search-email-text">{row.email || "—"}</span>
          {row.email && (
            <button
              type="button"
              className="org-search-email-copy"
              title="Copy email"
              onClick={(e) => { e.stopPropagation(); copyToClipboard(row.email); }}
            >
              <FontAwesomeIcon icon={faCopy} />
            </button>
          )}
        </div>
      ),
    },
    {
      name: "Country Code",
      width: "120px",
      sortable: true,
      selector: (row) => getCountry(row)?.code || "—",
    },
  ];

  if (loading && organisations.length === 0) {
    return <GoldLoader label="Loading..." variant="page" />;
  }

  return (
    <div className="org-search-page">
          <div className="d-flex justify-content-between align-items-center mb-2 ems-list-toolbar">
            <input
              className="form-control form-control-sm"
              style={{ maxWidth: 440 }}
              placeholder="Search by Org name, email, Org Type, country"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              title="Starts-with match on org name, email, org type, country code, and linked person name/email"
            />

            <button
              className="btn"
              style={{ background: "#D9A128", color: "#fff", borderRadius: 5, padding: "8px 12px", whiteSpace: "nowrap" }}
              onClick={async () => {
                if (!competitionId) {
                  alert(
                    "No active competition found. Mark one competition as active in EMS before assigning organisations."
                  );
                  return;
                }
                const org_ids = selectedRows.map((o) => o._id);
                if (!org_ids.length) { alert("No organisations selected"); return; }
                try {
                  await axios.post(`${API_URL}/ems/orgcompetitions/bulk-add`, {
                    org_ids,
                    competition_id: competitionId,
                  });
                  await refreshAssignedOrgIds(competitionId);
                  setSelectedRows([]);
                  alert(`${org_ids.length} organisations assigned successfully`);
                } catch (err) {
                  console.error(err);
                  alert("Failed to assign organisations");
                }
              }}
            >
              Assign Organisations to Competition
            </button>
          </div>

          <div className="org-search-table-shell">
            <DataTable
              keyField="_id"
              columns={columns}
              data={filteredData}
              progressPending={loading}
              progressComponent={<DataTableLoader />}
              pagination
              paginationPerPage={50}
              highlightOnHover
              customStyles={customStyles}
              selectableRows
              selectableRowDisabled={(row) => assignedOrgIds.has(String(row?._id))}
              onSelectedRowsChange={({ selectedRows: rows }) =>
                setSelectedRows(
                  rows.filter((row) => !assignedOrgIds.has(String(row?._id)))
                )
              }
            />
          </div>
    </div>
  );
};

export default OrganisationCompetitionSearch;
